import React, { useState, useRef, useEffect } from 'react';
import { MdClose, MdSend, MdVolumeUp, MdStop } from 'react-icons/md';
import { auth } from '../config/firebase';
import { fmtInstante } from '../utils/tempoCv';

// Format AI response: remove markdown, make it human-readable
const formatLyraText = (text: string): string => {
  return text
    // Remove bold markdown **text** and __text__
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    // Remove italic *text* and _text_
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    // Remove headers ## and ###
    .replace(/^#{1,4}\s+/gm, '')
    // Convert markdown bullet points to clean bullets
    .replace(/^[-*]\s+/gm, '• ')
    // Convert numbered lists (1. 2. etc) to clean format
    .replace(/^\d+\.\s+/gm, (match) => match)
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`(.*?)`/g, '$1')
    // Remove links [text](url) → text
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'lyra';
  timestamp: Date;
}

interface LyraChatbotProps {
  position?: 'sidebar' | 'floating';
  expanded?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';
const LYRA_API_URL = `${API_URL}/v1/public/lyra/chat`;

const LyraChatbot: React.FC<LyraChatbotProps> = ({ position = 'floating', expanded: sidebarExpanded = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Ola! Sou a Lyra, a assistente virtual da Pagali. Como posso ajudar-te hoje?',
      sender: 'lyra',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Allow other components (e.g. Hub Recursos) to open Lyra via custom event
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-lyra', handler);
    return () => window.removeEventListener('open-lyra', handler);
  }, []);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(LYRA_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.text,
          sessionId: localStorage.getItem('lyra_session_id') || Date.now().toString(),
          userId: auth.currentUser?.uid || null,
          timestamp: new Date().toISOString(),
        }),
      });

      // Store session ID for conversation continuity
      if (!localStorage.getItem('lyra_session_id')) {
        localStorage.setItem('lyra_session_id', Date.now().toString());
      }

      const data = await response.json();

      const lyraResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: data.response || data.message || data.output || 'Desculpa, nao consegui processar a tua mensagem. Tenta novamente.',
        sender: 'lyra',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, lyraResponse]);
    } catch (error) {
      console.error('Error sending message to Lyra:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Desculpa, estou com dificuldades tecnicas. Tenta novamente mais tarde.',
        sender: 'lyra',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Sidebar button version
  if (position === 'sidebar') {
    return (
      <>
        <button
          onClick={() => setIsOpen(true)}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-pagali-gray-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 hover:text-purple-600 ${!sidebarExpanded ? 'justify-center' : ''}`}
          title={!sidebarExpanded ? 'Lyra AI' : undefined}
        >
          <img
            src="/lyra.png"
            alt="Lyra"
            className="w-7 h-7 rounded-full object-cover flex-shrink-0 ring-2 ring-purple-200"
          />
          {sidebarExpanded && (
            <span className="text-sm font-medium bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Lyra AI
            </span>
          )}
        </button>

        {/* Chat Modal */}
        {isOpen && <ChatWindow messages={messages} inputValue={inputValue} setInputValue={setInputValue} isLoading={isLoading} sendMessage={sendMessage} handleKeyPress={handleKeyPress} onClose={() => setIsOpen(false)} messagesEndRef={messagesEndRef} inputRef={inputRef} />}
      </>
    );
  }

  // Floating button version (for landing page)
  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 group"
          aria-label="Abrir chat com Lyra"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity animate-pulse"></div>
            <div className="relative bg-white p-1 rounded-full shadow-xl border-2 border-purple-200 group-hover:border-purple-400 transition-all group-hover:scale-110">
              <img
                src="/lyra.png"
                alt="Lyra AI Assistant"
                className="w-14 h-14 rounded-full object-cover"
              />
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
          </div>
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Falar com Lyra
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && <ChatWindow messages={messages} inputValue={inputValue} setInputValue={setInputValue} isLoading={isLoading} sendMessage={sendMessage} handleKeyPress={handleKeyPress} onClose={() => setIsOpen(false)} messagesEndRef={messagesEndRef} inputRef={inputRef} />}
    </>
  );
};

// Separate ChatWindow component for reusability
interface ChatWindowProps {
  messages: Message[];
  inputValue: string;
  setInputValue: (value: string) => void;
  isLoading: boolean;
  sendMessage: () => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  onClose: () => void;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLInputElement>;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  inputValue,
  setInputValue,
  isLoading,
  sendMessage,
  handleKeyPress,
  onClose,
  messagesEndRef,
  inputRef,
}) => {
  const [playingId, setPlayingId] = React.useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const playAudio = async (messageId: string, text: string) => {
    // Stop if already playing
    if (playingId === messageId) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }

    setPlayingId(messageId);
    try {
      const response = await fetch(LYRA_API_URL.replace('/chat', '/tts'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: formatLyraText(text) }),
      });
      const data = await response.json();
      if (data.success && data.audio) {
        const audio = new Audio(`data:audio/mpeg;base64,${data.audio}`);
        audioRef.current = audio;
        audio.onended = () => setPlayingId(null);
        audio.play();
      } else {
        setPlayingId(null);
      }
    } catch {
      setPlayingId(null);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="/lyra.png"
            alt="Lyra"
            className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
          />
          <div>
            <h3 className="text-white font-semibold">Lyra</h3>
            <p className="text-white/70 text-xs">Assistente Virtual Pagali</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Fechar chat"
        >
          <MdClose className="text-xl" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                message.sender === 'user'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-br-md'
                  : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.sender === 'lyra' ? formatLyraText(message.text) : message.text}</p>
              <div className={`flex items-center justify-between mt-1`}>
                <p className={`text-xs ${message.sender === 'user' ? 'text-white/60' : 'text-gray-400'}`}>
                  {fmtInstante(message.timestamp, { hour: '2-digit', minute: '2-digit' }, 'pt-PT')}
                </p>
                {message.sender === 'lyra' && (
                  <button
                    onClick={() => playAudio(message.id, message.text)}
                    className={`p-1 rounded-full transition-colors ${
                      playingId === message.id
                        ? 'text-purple-600 bg-purple-100'
                        : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                    }`}
                    title={playingId === message.id ? 'Parar' : 'Ouvir'}
                  >
                    {playingId === message.id ? <MdStop className="text-sm" /> : <MdVolumeUp className="text-sm" />}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 bg-white border-t border-gray-100">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Escreve a tua mensagem..."
            className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white transition-all"
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Enviar mensagem"
          >
            <MdSend className="text-lg" />
          </button>
        </div>
        <p className="text-xs text-gray-400 text-center mt-2">
          Powered by Linda AI Studio
        </p>
      </div>
    </div>
  );
};

export default LyraChatbot;
